import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Clock, Globe, Zap } from "lucide-react";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import HeroGraphic from "../components/HeroGraphic";
import ContactForm from "../components/ContactForm";
import { AREAS, MACROAREE } from "../content/services";

const ease = [0.22, 1, 0.36, 1];
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease },
});
const rise = (delay = 0) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.75, delay, ease },
});

const ADVANTAGES = [
  {
    icon: Globe,
    title: "Operiamo in tutta Italia",
    text: "Gestiamo soprattutto pratiche e consulenze tecniche a distanza, senza limitarci al territorio locale.",
  },
  {
    icon: Clock,
    title: "Risposta entro 24 ore",
    text: "Esaminiamo rapidamente ogni richiesta e rispondiamo entro 24 ore.",
  },
  {
    icon: Zap,
    title: "Rapidità ed efficienza",
    text: "Ogni pratica viene affrontata con precisione, professionalità e attenzione ai tempi.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Invia la tua richiesta",
    text: "Descrivici brevemente la pratica o il servizio di cui hai bisogno.",
  },
  {
    n: "2",
    title: "Prima valutazione gratuita",
    text: "Esaminiamo la richiesta e facciamo insieme una prima valutazione, senza costi e senza impegno.",
  },
  {
    n: "3",
    title: "Ti indichiamo come procedere",
    text: "Ti ricontattiamo rapidamente, definiamo ciò che occorre e, se necessario, predisponiamo la proposta per l’attività da svolgere.",
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const yGraphic = useTransform(scrollY, [0, 600], [0, 36]);

  return (
    <>
      <Seo path="/" />
      {/* HERO */}
      <section className="relative overflow-hidden bg-paper" data-testid="section-hero">
        <div className="container-site grid gap-12 pb-16 pt-10 sm:pt-14 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-10 lg:pb-24 lg:pt-20">
          <div>
            <motion.p {...fade(0)} className="eyebrow" data-testid="hero-eyebrow">
              DN Studio Tecnico
            </motion.p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-antracite sm:text-5xl lg:text-[3.5rem]">
              <span className="block overflow-hidden">
                <motion.span className="block" {...rise(0.08)}>
                  Il tuo studio tecnico,
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span className="block text-petrolio" {...rise(0.18)}>
                  ovunque tu sia.
                </motion.span>
              </span>
            </h1>
            <motion.p {...fade(0.32)} className="mt-6 max-w-xl text-base leading-relaxed text-muted sm:text-lg">
              Pratiche tecniche, catastali, immobiliari e professionali gestite in tutta Italia.
            </motion.p>
            <motion.p {...fade(0.4)} className="mt-4 max-w-xl text-sm leading-relaxed text-antracite-soft sm:text-base">
              <strong className="font-semibold text-antracite">Rapidi, efficienti e professionali.</strong> Inviaci la tua
              richiesta: la prima valutazione è gratuita e ti rispondiamo entro 24 ore.
            </motion.p>
            <motion.div {...fade(0.48)} className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <a href="#richiedi-valutazione" data-testid="hero-cta-primary" className="btn-primary">
                Richiedi una valutazione gratuita
              </a>
              <Link to="/servizi" data-testid="hero-cta-secondary" className="btn-secondary">
                Scopri i servizi
              </Link>
            </motion.div>
            <motion.p {...fade(0.55)} className="mt-4 text-xs text-muted">
              La richiesta non comporta alcun impegno. Nessun pagamento online.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.25, ease }}
            style={{ y: yGraphic }}
            className="relative mx-auto w-full max-w-[420px] sm:max-w-[520px] lg:max-w-[560px] lg:justify-self-end"
          >
            <div className="lg:scale-[1.12]">
              <HeroGraphic />
            </div>
            <div
              className="absolute -bottom-4 left-4 flex items-center gap-2.5 rounded-full border border-line bg-white px-4 py-2.5 shadow-[0_4px_20px_rgba(23,63,58,0.1)] sm:left-8"
              data-testid="hero-badge-24h"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-salvia opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-petrolio" />
              </span>
              <span className="text-xs font-semibold text-antracite">Risposta entro 24 ore</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRE VANTAGGI */}
      <section className="py-16 lg:py-24" data-testid="section-vantaggi">
        <div className="container-site grid gap-5 md:grid-cols-3">
          {ADVANTAGES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-line bg-white p-7 card-hover" data-testid={`advantage-card-${i + 1}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-petrolio-light text-petrolio">
                  <a.icon size={22} strokeWidth={1.8} />
                </div>
                <h2 className="mt-5 font-display text-lg font-bold text-antracite">{a.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVIZI PRINCIPALI */}
      <section className="border-y border-line bg-fog py-16 lg:py-24" data-testid="section-servizi">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Servizi</p>
            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              Servizi tecnici per immobili, pratiche e documentazione
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
              Un unico interlocutore per le pratiche riguardanti il tuo immobile, gestite con precisione e tempi rapidi,
              anche a distanza.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {MACROAREE.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-line bg-white p-7 card-hover"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-petrolio-light text-petrolio">
                      <s.icon size={22} strokeWidth={1.8} />
                    </div>
                    <span className="text-xs font-semibold text-muted/70">0{i + 1}</span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-antracite transition-colors group-hover:text-petrolio">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{s.text}</p>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-petrolio">
                    Approfondisci
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/servizi" data-testid="home-cta-servizi" className="btn-secondary">
              Scopri tutti i servizi
            </Link>
          </Reveal>

          {/* TUTTI I SERVIZI — panoramica compatta per categoria */}
          <div className="mt-14 border-t border-line pt-10" data-testid="section-tutti-servizi">
            <Reveal>
              <h2 className="font-display text-xl font-bold text-antracite sm:text-2xl">Tutti i servizi</h2>
              <div className="mt-7 space-y-4">
                {AREAS.map((a) => (
                  <div key={a.id} className="grid gap-2 sm:grid-cols-[215px_1fr] sm:gap-8" data-testid={`all-services-${a.id}`}>
                    <Link
                      to={a.path}
                      data-testid={`all-services-cat-${a.id}`}
                      className="pt-1 text-[13px] font-semibold text-petrolio underline-offset-4 transition-colors hover:underline"
                    >
                      {a.label}
                    </Link>
                    <ul className="flex flex-wrap gap-2">
                      {a.services.map((s, i) => (
                        <li key={s}>
                          <Link
                            to={a.path}
                            data-testid={`service-tag-${a.id}-${i}`}
                            className="inline-block rounded-full border border-line bg-white px-3.5 py-1.5 text-[13px] leading-none text-antracite-soft transition-colors hover:border-salvia hover:text-petrolio"
                          >
                            {s}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="py-16 lg:py-24" data-testid="section-come-funziona">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Come funziona</p>
            <h2 className="mt-3 max-w-3xl font-display text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              Una prima valutazione gratuita, poi decidiamo insieme come procedere.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative border-t-2 border-line pt-6" data-testid={`step-${s.n}`}>
                  <span className="font-display text-4xl font-extrabold text-salvia">{s.n}</span>
                  <h3 className="mt-3 font-display text-lg font-bold text-antracite">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <div
              className="inline-flex items-center gap-2.5 rounded-full bg-petrolio-light px-5 py-3 text-sm font-semibold text-petrolio"
              data-testid="badge-24h"
            >
              <Clock size={16} />
              Rispondiamo alle richieste entro 24 ore.
            </div>
          </Reveal>
        </div>
      </section>

      {/* OPERATIVITÀ NAZIONALE */}
      <section className="bg-petrolio py-14 text-paper lg:py-20" data-testid="section-nazionale">
        <div className="container-site grid items-center gap-6 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Pratiche tecniche gestite a distanza, in tutta Italia.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-2xl text-sm leading-relaxed text-paper/80 sm:text-base">
              Molte pratiche tecniche e documentali possono essere gestite interamente a distanza. Quando è necessaria una
              verifica sul posto, valutiamo il sopralluogo o l’intervento tecnico sul territorio.
            </p>
          </Reveal>
        </div>
      </section>

      {/* RAPIDITÀ */}
      <section className="py-16 lg:py-24" data-testid="section-rapidita">
        <div className="container-site grid items-start gap-8 lg:grid-cols-2">
          <Reveal>
            <h2 className="max-w-lg font-display text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              Tempi rapidi, senza rinunciare alla precisione.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xl text-sm leading-relaxed text-muted sm:text-base">
              Inviaci la tua richiesta online. La esaminiamo e ti rispondiamo entro 24 ore. Per diverse pratiche
              documentali, la gestione può essere particolarmente rapida.
            </p>
            <a
              href="#richiedi-valutazione"
              data-testid="home-cta-rapida"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-petrolio transition-colors hover:text-petrolio-deep"
            >
              Invia la tua richiesta <ArrowRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="richiedi-valutazione" className="scroll-mt-20 border-t border-line bg-fog py-16 lg:py-24" data-testid="section-form">
        <div className="container-site grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <p className="eyebrow">Richiedi una valutazione</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              Descrivici la tua pratica
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted sm:text-base">
              La prima valutazione è gratuita e senza impegno. Ti rispondiamo entro 24 ore.
            </p>
            <ul className="mt-8 space-y-3.5">
              {["Prima valutazione gratuita e senza impegno", "Risposta entro 24 ore", "Nessun pagamento online"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-antracite-soft">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-petrolio-light text-petrolio">
                    <Check size={13} strokeWidth={2.5} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-line bg-white p-6 shadow-[0_4px_30px_rgba(23,63,58,0.05)] sm:p-8">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
